import {
  NSSampleSchema,
  SchemaSearchForm,
  applyClientLookupsToSchema,
  deriveAccordionFieldMapFromSchema,
  expandAccordionsByErrorPaths,
  sampleClientLookups,
  useLookupOptions,
  useRawLookupOptions,
  validateAndSubmitSearchForm,
  DocumentationTypes,
} from '@frontend/pricing-search';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getSearchFormSchema,
  prepareLoanDocTypes,
} from '../../../common/app-helpers';
import {
  calculateGovie,
  getClientSettingsNonLoanValues,
  getStateCounties,
  getZipLocations,
} from '../../../common/services/searchFormServices';
import { useIsZeroBasedPricing } from '../../../common/utils/customHooks/useIsZeroBasedPricing';
import { usePricingSearchConfigurations } from '../../../data/useSelectors';
import {
  PATH_SCENARIO_CHANNEL,
  PATH_SCENARIO_RESULTS,
} from '../../../route/constants';
import { AppDispatch } from '../../../store';
import { searchFormActions } from '../../../store/searchForm';
import { setLoanInfo } from '../../components/Form/utils/common';
import { ErrorBanner } from '../../SearchForm/components/ErrorBanner';
import ScenarioFooter from '../components/ScenarioFooter';
import { ScenarioPageContent } from '../components/styled';
import { useScenarioContext } from '../hooks/useScenarioContext';
import { ChannelOption } from '../components/ScenarioHeader/constants';
import { useExpandAccordionsOnError } from '../../components/Form/utils/customHooks';

export const ScenarioSearch: React.FC<{ schemaForm: any }> = ({
  schemaForm,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedChannel } = useScenarioContext();
  const isZeroBasedPricing = useIsZeroBasedPricing();
  const documentationTypes = useLookupOptions('documentationTypes') || [];

  const searchConfigurations = usePricingSearchConfigurations();

  const schema = useMemo(() => {
    if (searchConfigurations && selectedChannel?.value) {
      const loanData = {
        loanInformation: { loanChannel: selectedChannel.value },
      };
      return (
        getSearchFormSchema(loanData, searchConfigurations, null) ||
        NSSampleSchema
      );
    }
    return NSSampleSchema;
  }, [searchConfigurations, selectedChannel?.value]);

  const [activeAccordions, setActiveAccordions] = useState<
    Record<number, boolean>
  >({});

  const allLookups = useRawLookupOptions();
  const schemaWithLookups = useMemo(() => {
    if (!schema) return NSSampleSchema;
    return applyClientLookupsToSchema(
      schema,
      allLookups || sampleClientLookups,
    );
  }, [schema, allLookups]);

  const accordionFieldMap = useMemo(
    () => deriveAccordionFieldMapFromSchema(schemaWithLookups),
    [schemaWithLookups],
  );

  const applyScenarioSpecificPreseeding = (
    loanInfo: any,
    selectedChannel: ChannelOption,
    documentationTypes: DocumentationTypes[],
  ) => {
    // Pre-seed some values based on channel selection that we don't get from Partner API OriginID
    loanInfo.loanInformation = loanInfo.loanInformation ?? {};
    loanInfo.nonLoanValues = loanInfo.nonLoanValues ?? {};
    loanInfo.property = loanInfo.property ?? {};
    loanInfo.borrowerFinancialHistory = loanInfo.borrowerFinancialHistory ?? {};
    loanInfo.compensation = loanInfo.compensation ?? {};
    loanInfo.loanInformation.loanChannel = selectedChannel.value;
    loanInfo.loanInformation.wsChannel = selectedChannel.wsChannel;
    loanInfo.nonLoanValues.loanChannel = selectedChannel.value;
    loanInfo.property.tax = loanInfo.property.tax ?? 0;
    loanInfo.property.insuranceAmount = loanInfo.property.insuranceAmount ?? 0;
    loanInfo.property.associationFee = loanInfo.property.associationFee ?? 0;
    loanInfo.property.rentalIncome = loanInfo.property.rentalIncome ?? 0;
    loanInfo.loanInformation.cashOut = loanInfo.loanInformation.cashOut ?? 0;
    loanInfo.loanInformation.financedAmount =
      loanInfo.loanInformation.financedAmount ?? 0;
    loanInfo.documentationLevel = loanInfo.documentationLevel ?? '1';
    loanInfo.compensation.model = loanInfo.compensation.model ?? 1;
    loanInfo.lockDays = loanInfo.lockDays ?? 30;
    loanInfo.borrowerFinancialHistory.nonQMDocLevel = '1';
    prepareLoanDocTypes(loanInfo, documentationTypes);
    return loanInfo;
  };

  const { handleSearchFormInitialized } = useExpandAccordionsOnError({
    formReady: schemaForm,
    schemaWithLookups,
    schemaForm,
    accordionFieldMap,
    setActiveAccordions,
  });

  const preseededChannelRef = useRef<string | null>(null);

  useEffect(() => {
    if (preseededChannelRef.current === selectedChannel?.value) return;
    const loanInfo = schemaForm.getValues();
    applyScenarioSpecificPreseeding(
      loanInfo,
      selectedChannel!,
      documentationTypes,
    );
    schemaForm.reset(loanInfo);
    preseededChannelRef.current = selectedChannel?.value ?? null;
  }, [schemaForm, selectedChannel, documentationTypes]);

  // Merged rather than reset so the channel pre-seed above is not overwritten
  useEffect(() => {
    schemaForm.setValue('nonLoanValues', {
      ...schemaForm.getValues('nonLoanValues'),
      ...getClientSettingsNonLoanValues(allLookups),
    });
  }, [schemaForm, allLookups]);

  const handleQualify = async () => {
    const result = await validateAndSubmitSearchForm({
      schema: schemaWithLookups,
      schemaForm,
      preview: false,
    });

    if (result.valid) {
      dispatch(setLoanInfo(result.formData) as any);
      dispatch(searchFormActions.setBannerIsOpen(false));
      navigate(PATH_SCENARIO_RESULTS);
    } else {
      console.error('Erroring form paths', result.errorPaths);
      dispatch(searchFormActions.setBannerIsOpen(true));
      if (result.errorPaths?.length) {
        const toOpen = expandAccordionsByErrorPaths(
          accordionFieldMap,
          result.errorPaths,
        );
        setActiveAccordions((prev) => ({ ...prev, ...toOpen }));
      }
    }
  };

  return (
    <>
      <ErrorBanner />
      <ScenarioPageContent>
        {schemaForm && (
          <SchemaSearchForm
            schema={schemaWithLookups}
            schemaForm={schemaForm}
            preview={false}
            getStateCounties={getStateCounties}
            getZipLocations={getZipLocations}
            calculateGovie={calculateGovie}
            activeAccordions={activeAccordions}
            setActiveAccordions={setActiveAccordions}
            isZeroBasedPricing={isZeroBasedPricing}
            onInitialized={handleSearchFormInitialized}
          />
        )}
      </ScenarioPageContent>
      <ScenarioFooter
        onCancel={() => navigate(PATH_SCENARIO_CHANNEL)}
        onNext={handleQualify}
        nextLabel="Qualify"
      />
    </>
  );
};

export default ScenarioSearch;
